import {
  ProductCatalogColorGroup,
  ProductCatalogProductColorOption,
} from '../../__generated__/graphql/catalog/graphql'
import {
  PricingInformation,
  derivePricingForVariantGroup,
} from '../_components/pricing/PricingRange.client'

type RequiredColorGroupFields = Pick<ProductCatalogColorGroup, 'displayValue' | 'id'>
export type RequiredColorOptionFields = {
  outOfStock: ProductCatalogProductColorOption['outOfStock']
  color: {
    id: ProductCatalogProductColorOption['color']['id']
    displayValue: ProductCatalogProductColorOption['color']['displayValue']
    hexCodes: ProductCatalogProductColorOption['color']['hexCodes']
    group: ProductCatalogProductColorOption['color']['group']
    swatchImage?: {
      url: NonNullable<ProductCatalogProductColorOption['color']['swatchImage']>['url']
    } | null
  }
}

type GroupedOptions = {
  group: RequiredColorGroupFields
  colorOptions: Array<RequiredColorOptionFields>
}

type MapOfGroupedOptions = Record<string, GroupedOptions>

export const groupColorOptionsByColorGroup = (
  colorOptions: RequiredColorOptionFields[]
): GroupedOptions[] => {
  const colorGroups: MapOfGroupedOptions = {}
  colorOptions.forEach(colorOption => {
    const colorGroup = colorOption.color.group?.id
      ? colorGroups[colorOption.color.group.id]
      : undefined

    // Check if the color ID already exists in the given color group
    const colorOptionExists = colorGroup?.colorOptions.find(
      co => co.color.id === colorOption.color.id
    )

    if (colorGroup) {
      if (!colorOptionExists) colorGroup.colorOptions.push(colorOption)
    } else if (colorOption.color.group?.id) {
      colorGroups[colorOption.color.group.id] = {
        group: colorOption.color.group,
        colorOptions: [colorOption],
      }
    }
  })
  return Object.values(colorGroups)
}

type PricingSupportedRequiredColorOptionFields = RequiredColorOptionFields & {
  compareAtPriceRange?: {
    min: {
      amount: number
    }
    max: {
      amount: number
    }
    minPercentageOff: number
    maxPercentageOff: number
  } | null
  priceRange: {
    currencyCode: string
    min: {
      amount: number
    }
    max: {
      amount: number
    }
  }
}

type PricingBucket = {
  id: string
  pricing: PricingInformation
  colorOptions: Array<PricingSupportedRequiredColorOptionFields>
}

type GroupedAndPricedOptions = {
  group: RequiredColorGroupFields
  colorOptions: Array<PricingSupportedRequiredColorOptionFields>
  pricingBuckets: Array<PricingBucket>
}

type MapOfGroupedAndPricedOptions = Record<string, GroupedAndPricedOptions>

export const groupColorOptionsByColorGroupAndPricing = (
  colorOptions: PricingSupportedRequiredColorOptionFields[]
): GroupedAndPricedOptions[] => {
  const pricedColorGroups: MapOfGroupedAndPricedOptions = {}
  colorOptions.forEach(colorOption => {
    const pricedColorGroup = colorOption.color.group?.id
      ? pricedColorGroups[colorOption.color.group.id]
      : undefined

    const colorPricing = derivePricingForVariantGroup(colorOption)

    if (pricedColorGroup) {
      const pricingBucket = pricedColorGroup.pricingBuckets.find(priceBucket =>
        isPricingInfoEqual(priceBucket.pricing, colorPricing)
      )
      pricedColorGroup.colorOptions.push(colorOption)
      if (pricingBucket) {
        pricingBucket.colorOptions.push(colorOption)
      } else {
        pricedColorGroup.pricingBuckets.push({
          id: hashPricingInfo(colorPricing),
          pricing: colorPricing,
          colorOptions: [colorOption],
        })

        pricedColorGroup.pricingBuckets.sort((thisBucket, thatBucket) => {
          return comparePricingInfo(thisBucket.pricing, thatBucket.pricing)
        })
      }
    } else if (colorOption.color.group?.id) {
      pricedColorGroups[colorOption.color.group.id] = {
        group: colorOption.color.group,
        colorOptions: [colorOption],
        pricingBuckets: [
          {
            id: hashPricingInfo(colorPricing),
            pricing: colorPricing,
            colorOptions: [colorOption],
          },
        ],
      }
    }
  })

  return Object.values(pricedColorGroups)
}

function hashPricingInfo(thisPricing: PricingInformation) {
  return `${thisPricing.priceRange.min}-${thisPricing.priceRange.max}-${thisPricing.discountPriceRange?.min}-${thisPricing.discountPriceRange?.max}`
}

function isPricingInfoEqual(thisPricing: PricingInformation, thatPricing: PricingInformation) {
  return hashPricingInfo(thisPricing) === hashPricingInfo(thatPricing)
}

function comparePricingInfo(thisPricing: PricingInformation, thatPricing: PricingInformation) {
  if (thisPricing.discountPriceRange && !thatPricing.discountPriceRange) {
    return -1
  } else if (thisPricing.discountPriceRange && thatPricing.discountPriceRange) {
    return thatPricing.discountPriceRange.min - thisPricing.discountPriceRange.min
  } else {
    return thatPricing.priceRange.min - thisPricing.priceRange.min
  }
}
